import React, { useCallback } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import {
  Control,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import renderController from '../lib/renderController';
import { mediumRoundedCorners } from '../RoundedCorners';
import grey from '../Palettes/grey';
import overlay from '../Palettes/overlay';
import colors from '../Theme/colors';

const FileUploadWrapper = styled.div`
  margin: 0 0 0 4px;
`;

export const FileUploadLabel = styled.label`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.sizes.h6};
  color: ${overlay[900]};
  margin: 0 0 6px 2px;
  display: block;
  opacity: 1;
  line-height: 1;
`;

export const FileUploadHelpText = styled.div`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.sizes.subtitle1};
  color: ${grey[650]};
  margin: 6px 0 6px 2px;
`;

const FileUploadButton = styled.div`
  border-style: solid;
  border-width: 2px;
  border-color: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.primary.main};
  text-transform: uppercase;
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.sizes.button};
  letter-spacing: 1px;
  font-weight: 500;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  min-width: 64px;
  height: 36px;
  overflow: hidden;
  vertical-align: middle;
  margin: 6px 0 0;
  cursor: pointer;
  ${mediumRoundedCorners};
`;

const FileUploadInput = styled.input`
  font-family: ${({ theme }) => theme.fonts.secondary};
  color: ${overlay[600]};
  display:block;
  margin-bottom: 4px;
  opacity: 0;
  height: 0;
  position: absolute;
`;

const FileUploadFileName = styled.div`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.sizes.body1};
  color: ${colors.black};
  margin: 0 0 0 2px;
`;

const preventDefaultAndStopPropagation = (
  event: React.DragEvent<HTMLDivElement>,
) => {
  event.stopPropagation();
  event.preventDefault();
};

const fileUploadPropertyTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func,
  htmlId: PropTypes.string,
  label: PropTypes.string,
  helpText: PropTypes.string,
  filename: PropTypes.string,
  accept: PropTypes.string,
  ariaLabel: PropTypes.string,
};

type FileUploadProperties = Omit<
  InferProps<typeof fileUploadPropertyTypes>,
  'onChange'
> & {
  onChange?: (event: {
    target: { files: FileList | null };
  }) => void;
};

const FileUpload = React.forwardRef(
  ({
    label,
    ariaLabel,
    helpText,
    htmlId,
    className,
    onChange,
    filename,
    accept,
  }: FileUploadProperties, reference: React.Ref<HTMLInputElement>) => (
    <FileUploadWrapper
      className={className ?? undefined}
      onDragOver={preventDefaultAndStopPropagation}
      onDragEnter={preventDefaultAndStopPropagation}
      onDragLeave={preventDefaultAndStopPropagation}
      onDrop={useCallback((
        event: React.DragEvent<HTMLDivElement>,
      ) => {
        preventDefaultAndStopPropagation(event);

        onChange?.({
          target: { files: event.dataTransfer.files },
        });
      }, [onChange])}
    >
      <FileUploadLabel>
        {label}
        <FileUploadHelpText>
          {helpText}
        </FileUploadHelpText>
        <FileUploadButton>
          Select a file
        </FileUploadButton>
        <FileUploadInput
          id={htmlId ?? 'fileUpload'}
          type="file"
          accept={accept ?? undefined}
          onChange={onChange}
          ref={reference}
          aria-label={ariaLabel ?? undefined}
        />
      </FileUploadLabel>
      <FileUploadFileName>
        {filename ?? 'No File Selected.'}
      </FileUploadFileName>
    </FileUploadWrapper>
  ),
);

FileUpload.propTypes = fileUploadPropertyTypes;

const fileUploadWithControllerPropertyTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.shape({}).isRequired,
  rules: PropTypes.shape({}).isRequired,
  noFileSelectedLabel: PropTypes.string,
  className: PropTypes.string,
  htmlId: PropTypes.string,
  label: PropTypes.string,
  helpText: PropTypes.string,
  accept: PropTypes.string,
  ariaLabel: PropTypes.string,
};

type FileUploadWithControllerProperties<
  T extends FieldValues = FieldValues,
> = Omit<
  InferProps<typeof fileUploadWithControllerPropertyTypes>,
  'control' | 'rules'
> & {
  control: Control<T>;
  rules?: UseControllerProps['rules'];
};

export const FileUploadWithController = <T extends FieldValues = FieldValues>({
  name,
  control,
  rules,
  noFileSelectedLabel,
  label,
  helpText,
  htmlId,
  accept,
  ariaLabel,
  className,
}: FileUploadWithControllerProperties<T>) => {
  const render = useCallback(
    ({
      field: { onChange, value, ref },
    }: {

      field: ControllerRenderProps<T, FieldPath<T>>;
    }) => (
      <FileUpload
        onChange={({ target: { files } }) => onChange(
          files?.[0],
        )}
        filename={
          value?.name
            ?? (noFileSelectedLabel ?? 'No File Selected.')
        }
        ref={ref}
        label={label}
        helpText={helpText}
        htmlId={htmlId}
        accept={accept}
        ariaLabel={ariaLabel}
        className={className}
      />
    ),
    [
      noFileSelectedLabel,
      label,
      helpText,
      htmlId,
      accept,
      ariaLabel,
      className,
    ],
  );

  return renderController<T>({
    control, name, rules, render,
  });
};

FileUploadWithController
  .propTypes = fileUploadWithControllerPropertyTypes;

export default FileUpload;
